import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { SuppressionFileResponse, CampaignResponse } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';
import { campaignStyleEnumToLabel } from '../lib/campaign-styles';

export function Suppression() {
  const [files, setFiles] = useState<SuppressionFileResponse[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const { success, error: toastError } = useToast();

  const loadCampaigns = () => {
    api
      .getCampaigns()
      .then((campaignsData) => {
        setCampaigns(campaignsData);
      })
      .catch((err: ApiError) => setError(err.message));
  };

  const loadFilesForCampaign = (campaignId: string) => {
    if (!campaignId) {
      setFiles([]);
      return;
    }
    api
      .getSuppressionFiles(campaignId)
      .then((filesData) => {
        setFiles(filesData);
      })
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    loadFilesForCampaign(selectedCampaignId);
  }, [selectedCampaignId]);

  const handleDelete = async (fileId: string) => {
    if (!selectedCampaignId) {
      toastError('Please select a campaign');
      return;
    }

    // Confirm before deleting
    if (!confirm('Delete this suppression file?')) {
      return;
    }

    setDeletingFileId(fileId);
    try {
      await api.deleteSuppressionFile(selectedCampaignId, fileId);
      success('Suppression file deleted successfully');
      // Reload files for the campaign
      loadFilesForCampaign(selectedCampaignId);
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete suppression file');
    } finally {
      setDeletingFileId(null);
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading suppression files: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Suppression Files</h1>
        <p className="text-muted-foreground mt-2">Manage suppression files for your campaigns</p>
        <p className="text-sm text-muted-foreground mt-1">
          Upload suppression files from the campaign&apos;s Suppression section (saved with
          Update/Create).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {selectedCampaignId
              ? `Uploaded Files for ${campaigns.find((c) => c.id === selectedCampaignId)?.name || 'Selected Campaign'}`
              : 'Uploaded Files'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4 max-w-md">
            <Label htmlFor="campaign">Campaign</Label>
            <Select
              id="campaign"
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
            >
              <option value="">Select a campaign...</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                  {campaign.campaignStyle
                    ? ` (${campaignStyleEnumToLabel(campaign.campaignStyle)})`
                    : ''}
                </option>
              ))}
            </Select>
          </div>
          {!selectedCampaignId ? (
            <div className="text-center text-muted-foreground py-8">
              Select a campaign above to view its suppression files.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Row Count</TableHead>
                  <TableHead>Hash Algorithm</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Uploaded At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No suppression files uploaded for this campaign yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  files.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell className="font-medium">{file.originalFilename}</TableCell>
                      <TableCell>{file.rowCount ?? 'Unknown'}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {file.hashAlgorithm}
                        </span>
                      </TableCell>
                      <TableCell>{file.uploadedBy}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(file.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(file.id)}
                          disabled={deletingFileId === file.id}
                        >
                          {deletingFileId === file.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
